import configureMeasurements, { Converter, Measure, UnitCache } from 'convert-units';
import angle, { AngleSystems, AngleUnits } from 'convert-units/definitions/angle';
import frequency, { FrequencyUnits } from 'convert-units/definitions/frequency';
import length, { LengthSystems, LengthUnits } from 'convert-units/definitions/length';
import pressure, { PressureSystems, PressureUnits } from 'convert-units/definitions/pressure';
import speed, { SpeedSystems, SpeedUnits } from 'convert-units/definitions/speed';
import temperature, { TemperatureSystems, TemperatureUnits } from 'convert-units/definitions/temperature';
import time, { TimeSystems, TimeUnits } from 'convert-units/definitions/time';

export const SALTWATER_FLUID_DENSITY = 1024.76; // kg/m^3, assumes 20°C and 35 PSU salinity
export const FRESHWATER_FLUID_DENSITY = 998.21; // kg/m^3, assumes 20°C
const GRAV_ACCELERATION = 9.81; // m/s^2
const STANDARD_ATMOSPHERIC_PRESSURE = 101325; // Pascals

// Measures: The names of the measures being used
export type Measures =
    | 'length'
    | 'angle'
    | 'temperature'
    | 'pressure'
    | 'speed'
    | 'frequency'
    | 'time'
    | 'precipitationRate'
    | 'directionalWaveEnergy'
    | 'massConcentration'
    | 'density'
    | 'salinity'
    | 'turbidity'
    | 'ratio'
    | 'soundPressureLevel';

// Systems: The systems being used across all measures
type Systems = LengthSystems | AngleSystems | TemperatureSystems | PressureSystems | SpeedSystems | TimeSystems;

// Units: All the units across all measures and their systems
export type Units =
    | ExtendedTemperatureUnits
    | ExtendedSpeedUnits
    | FrequencyUnits
    | ExtendedLengthUnits
    | AngleUnits
    | ExtendedPressureUnits
    | PrecipitationRateUnits
    | DirectionalWaveEnergyUnits
    | TimeUnits
    | MassConcentrationUnits
    | DensityUnits
    | SalinityUnits
    | TurbidityUnits
    | SoundPressureLevelUnits
    | RatioUnits;

// Extend the temperature measure to include °C and °F unit aliases which is something wavefleet API uses along with C and F
export type ExtendedTemperatureUnits = TemperatureUnits | '°C' | '°F';
const extendedTemperature: Measure<TemperatureSystems, ExtendedTemperatureUnits> = {
    systems: {
        metric: {
            ...temperature.systems.metric,
            '°C': {
                name: {
                    singular: 'Degree Celsius',
                    plural: 'Degrees Celsius',
                },
                to_anchor: 1,
            },
        },
        imperial: {
            ...temperature.systems.imperial,
            '°F': {
                name: {
                    singular: 'Degree Fahrenheit',
                    plural: 'Degrees Fahrenheit',
                },
                to_anchor: 1,
            },
        },
    },
    anchors: {
        ...temperature.anchors,
    },
};

// Extend the speed measure to include "knots" alias which is something wavefleet API uses along with m/s and mph
// cm/s is reported from some sensors; 'm / s' and 'kt' are returned by the weather API
export type ExtendedSpeedUnits = SpeedUnits | 'knots' | 'kt' | 'cm/s' | 'm / s';
const extendedSpeed: Measure<SpeedSystems, ExtendedSpeedUnits> = {
    systems: {
        imperial: {
            ...speed.systems.imperial,
            knots: speed.systems.imperial?.knot,
            kt: speed.systems.imperial?.knot,
        },
        metric: {
            ...speed.systems.metric,
            'cm/s': {
                name: {
                    singular: 'Centimetres per second',
                    plural: 'Centimetre per second',
                },
                to_anchor: 0.036, // kph
            },
            'm / s': speed.systems.metric?.['m/s'],
        },
    },
    anchors: {
        ...speed.anchors,
    },
};

// Extend the length measure to include "NM" which the weather API uses for nautical miles
export type ExtendedLengthUnits = LengthUnits | 'NM';
const extendedLength: Measure<LengthSystems, ExtendedLengthUnits> = {
    systems: {
        imperial: {
            ...length.systems.imperial,
            NM: length.systems.imperial?.nMi,
        },
        metric: {
            ...length.systems.metric,
        },
    },
    anchors: {
        ...length.anchors,
    },
};

// Extend pressure with some density-aware water pressure units (for determining depth from pressure),
// and μbar which is produced by some pressure sensors
export type ExtendedPressureUnits = PressureUnits | 'mH20-fresh' | 'mH20-salt' | 'μbar';
const extendedPressure: Measure<PressureSystems, ExtendedPressureUnits> = {
    systems: {
        imperial: {
            ...pressure.systems.imperial,
        },
        metric: {
            // anchor is 1 kPa
            ...pressure.systems.metric,
            μbar: {
                name: {
                    singular: 'microbar',
                    plural: 'microbar',
                },
                to_anchor: 1e-4,
            },
            'mH20-fresh': {
                name: {
                    singular: 'meter of fresh water @ 20°C',
                    plural: 'meters of fresh water @ 20°C',
                },
                to_anchor: (FRESHWATER_FLUID_DENSITY * GRAV_ACCELERATION) / 1000,
                anchor_shift: -STANDARD_ATMOSPHERIC_PRESSURE / 1000,
            },
            'mH20-salt': {
                name: {
                    singular: 'meter of salt water (35PSU) @ 20°C',
                    plural: 'meters of salt water (35PSU) @ 20°C',
                },
                to_anchor: (SALTWATER_FLUID_DENSITY * GRAV_ACCELERATION) / 1000,
                anchor_shift: -STANDARD_ATMOSPHERIC_PRESSURE / 1000,
            },
        },
    },
    anchors: {
        ...pressure.anchors,
    },
};

// These are equivalent to speed units but used specifically for precipitation, "kg" means "liter of water".
// We have both 'mm/hr' and 'mm/h' because the former is used as a weather variable unit and the latter is SI.
export type PrecipitationRateUnits = 'kg/(m^2 s)' | 'mm/hr' | 'mm/h' | 'in/h';
const precipitationRate = {
    systems: {
        metric: {
            'kg/(m^2 s)': {
                name: {
                    singular: 'Kilogram per square meter per second',
                    plural: 'Kilograms per square meter per second',
                },
                to_anchor: 3600,
            },
            'mm/h': {
                name: {
                    singular: 'Millimeter per hour',
                    plural: 'Millimeters per hour',
                },
                to_anchor: 1,
            },
            'mm/hr': {
                name: {
                    singular: 'Millimeter per hour',
                    plural: 'Millimeters per hour',
                },
                to_anchor: 1,
            },
        },
        imperial: {
            'in/h': {
                name: {
                    singular: 'Inch per hour',
                    plural: 'Inches per hour',
                },
                to_anchor: 1,
            },
        },
    },
    anchors: {
        metric: {
            imperial: {
                ratio: 1 / 25.4,
            },
        },
        imperial: {
            metric: {
                ratio: 25.4,
            },
        },
    },
};

export type DirectionalWaveEnergyUnits = 'cm^2/(Hz*Deg)' | 'in^2/(Hz*Deg)' | 'm^2/(Hz*Deg)';
const directionalWaveEnergy = {
    systems: {
        metric: {
            'm^2/(Hz*Deg)': {
                name: {
                    singular: 'Square meter per Hz-degree',
                    plural: 'Square meters per Hz-degree',
                },
                to_anchor: 10000,
            },
            'cm^2/(Hz*Deg)': {
                name: {
                    singular: 'Square centimeter per Hz-degree',
                    plural: 'Square centimeters per Hz-degree',
                },
                to_anchor: 1,
            },
        },
        imperial: {
            'in^2/(Hz*Deg)': {
                name: {
                    singular: 'Square inch per Hz-degree',
                    plural: 'Square inches per Hz-degree',
                },
                to_anchor: 1,
            },
        },
    },
    anchors: {
        metric: {
            imperial: {
                ratio: 1 / 6.4516,
            },
        },
        imperial: {
            metric: {
                ratio: 6.4516,
            },
        },
    },
};

export type MassConcentrationUnits = 'mg/L' | 'oz/cuft';
const massConcentration = {
    systems: {
        metric: {
            'mg/L': {
                name: {
                    singular: 'Milligram per liter',
                    plural: 'Milligrams per liter',
                },
                to_anchor: 1,
            },
        },
        imperial: {
            'oz/cuft': {
                name: {
                    singular: 'Ounce per cubic foot',
                    plural: 'Ounces per cubic feet',
                },
                to_anchor: 1,
            },
        },
    },
    anchors: {
        metric: {
            imperial: {
                ratio: 1 / 1001.15,
            },
        },
        imperial: {
            metric: {
                ratio: 1001.15,
            },
        },
    },
};

export type DensityUnits = 'kg/m^3' | 'lb/cuft';
const density = {
    systems: {
        metric: {
            'kg/m^3': {
                name: {
                    singular: 'Kilogram per cubic meter',
                    plural: 'Kilograms per cubic meter',
                },
                to_anchor: 1,
            },
        },
        imperial: {
            'lb/cuft': {
                name: {
                    singular: 'Pound per cubic foot',
                    plural: 'Pounds per cubic foot',
                },
                to_anchor: 1,
            },
        },
    },
    anchors: {
        metric: {
            imperial: {
                ratio: 1 / 16.0185,
            },
        },
        imperial: {
            metric: {
                ratio: 16.0185,
            },
        },
    },
};

export type SalinityUnits = 'PSU';
const salinity = {
    systems: {
        SI: {
            PSU: {
                name: {
                    singular: 'Practical salinity unit',
                    plural: 'Practical salinity units',
                },
                to_anchor: 1,
            },
        },
    },
};

export type TurbidityUnits = 'FTU';
const turbidity = {
    systems: {
        SI: {
            FTU: {
                name: {
                    singular: 'Formazin turbidity unit',
                    plural: 'Formazin turbidity units',
                },
                to_anchor: 1,
            },
        },
    },
};

export type RatioUnits = '%' | 'ratio';
export const ratio = {
    systems: {
        SI: {
            '%': {
                name: {
                    singular: 'Percent',
                    plural: 'Percent',
                },
                to_anchor: 0.01,
            },
            ratio: {
                name: {
                    singular: 'Ratio',
                    plural: 'Ratio',
                },
                to_anchor: 1,
            },
        },
    },
};

export type SoundPressureLevelUnits = 'dB' | 'dB re 1 µPa²' | 'dB re µPa²';
export const soundPressureLevel = {
    systems: {
        SI: {
            dB: {
                name: {
                    singular: 'Decibel',
                    plural: 'Decibels',
                },
                to_anchor: 1,
            },
            'dB re 1 µPa²': {
                name: {
                    singular: 'Decibel',
                    plural: 'Decibels',
                },
                to_anchor: 1,
            },
            'dB re µPa²': {
                name: {
                    singular: 'Decibel',
                    plural: 'Decibels',
                },
                to_anchor: 1,
            },
        },
    },
};

const measures = {
    temperature: extendedTemperature,
    speed: extendedSpeed,
    frequency,
    angle,
    length: extendedLength,
    pressure: extendedPressure,
    time,
    precipitationRate,
    directionalWaveEnergy,
    massConcentration,
    density,
    salinity,
    turbidity,
    ratio,
    soundPressureLevel,
};

const configuredConvert = configureMeasurements<Measures, Systems, Units>(measures);

/**
 * Always returns NaN. Used when convert is passed null or undefined (rather than defaulting
 * to 0 as the stock converter does).
 */
const nanConverter = new (class extends Converter<Measures, Systems, Units> {
    constructor() {
        super(measures, {} as UnitCache<Measures, Systems, Units>);
    }

    from(): this {
        return this;
    }

    to(): number {
        return NaN;
    }
})();

export const convert: typeof configuredConvert = (value) => {
    if (Number.isFinite(value)) return configuredConvert(value);

    return nanConverter;
};
